import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { updateUser } from 'features/user/store/userSlice'
import { useAbility } from 'providers/casl'
import { useQuery } from 'providers/tanstack-query/useQuery'
import { GET_CURRENT_WORKSPACE_GAM_CONNECTION } from './api/query'

import type { UserCurrentWorkspace } from 'features/user/types/user'
import type {
  GetCurrentWorkspaceGamConnectionQuery,
  GetCurrentWorkspaceGamConnectionQueryVariables,
} from 'gql/graphql'
import type { AppDispatch, RootState } from 'providers/redux'

type LatestGamConnectionOrder = UserCurrentWorkspace['latestGamConnectionOrder']

export const useLoadGamConnection = (): void => {
  const dispatch: AppDispatch = useDispatch()
  const ability = useAbility()
  const { user } = useSelector((state: RootState) => state.user)
  const currentWorkspace: undefined | UserCurrentWorkspace = user?.currentWorkspace

  const { data: latestGamConnectionOrder } = useQuery<
    GetCurrentWorkspaceGamConnectionQueryVariables,
    GetCurrentWorkspaceGamConnectionQuery,
    LatestGamConnectionOrder
  >({
    options: {
      enabled:
        currentWorkspace !== undefined &&
        currentWorkspace.latestGamConnectionOrder === undefined &&
        ability.can('access', 'GlobalSetupPage-googleAdManagerConnection'),
      select: ({ node }: GetCurrentWorkspaceGamConnectionQuery): LatestGamConnectionOrder =>
        node && 'latestGamConnectionOrder' in node
          ? (node.latestGamConnectionOrder ?? undefined)
          : undefined,
    },
    query: GET_CURRENT_WORKSPACE_GAM_CONNECTION,
    variables: { workspaceId: currentWorkspace?.id ?? '' },
  })

  useEffect(() => {
    if (
      currentWorkspace === undefined ||
      currentWorkspace.latestGamConnectionOrder !== undefined ||
      latestGamConnectionOrder === undefined
    ) {
      return
    }

    dispatch(updateUser({ currentWorkspace: { ...currentWorkspace, latestGamConnectionOrder } }))
  }, [currentWorkspace, latestGamConnectionOrder])
}
